<template>
  <div
    class="modal fade dq-ws-modal"
    :id="modalId"
    tabindex="-1"
    aria-hidden="true"
    aria-labelledby="dq-ws-modal-title"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content dq-ws-card">
        <div class="modal-header dq-ws-header border-0">
          <div class="d-flex align-items-start gap-3">
            <span class="dq-ws-icon" aria-hidden="true">
              <i class="ti ti-building-skyscraper"></i>
            </span>
            <div>
              <h5 class="modal-title mb-1" id="dq-ws-modal-title">
                Sign in to your workspace
              </h5>
              <p class="dq-ws-subtitle mb-0">
                Enter your company workspace URL to continue to login.
              </p>
            </div>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <form class="modal-body dq-ws-body" @submit.prevent="continueToWorkspace">
          <label class="form-label fw-semibold" for="dq-ws-slug-input">
            Workspace URL
          </label>

          <div class="dq-ws-input-wrap" :class="{ 'is-error': !!error, 'is-ok': status === 'found' }">
            <span class="dq-ws-scheme">https://</span>
            <input
              id="dq-ws-slug-input"
              ref="inputRef"
              v-model="slug"
              type="text"
              class="form-control dq-ws-input"
              placeholder="your-company"
              autocomplete="organization"
              spellcheck="false"
              :disabled="submitting"
              @input="onSlugInput"
            />
            <span class="dq-ws-domain">.{{ domainLabel }}</span>
          </div>

          <div class="dq-ws-preview mt-2">
            <i class="ti ti-link"></i>
            <span>{{ previewHost || `your-company.${domainLabel}` }}</span>
          </div>

          <div v-if="status === 'checking'" class="dq-ws-status text-muted mt-2">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            Checking workspace…
          </div>
          <div v-else-if="status === 'found'" class="dq-ws-status text-success mt-2">
            <i class="ti ti-circle-check me-1"></i>
            Workspace found — ready to sign in.
          </div>
          <div v-else-if="error" class="dq-ws-status text-danger mt-2">
            <i class="ti ti-alert-circle me-1"></i>
            {{ error }}
          </div>

          <div class="dq-ws-hint mt-3">
            <i class="ti ti-info-circle"></i>
            <span>
              Example:
              <strong>labenz-autoworks.{{ domainLabel }}</strong>
              — enter only
              <strong>labenz-autoworks</strong>
            </span>
          </div>

          <div class="d-grid gap-2 mt-4">
            <button
              type="submit"
              class="btn btn-primary btn-lg dq-ws-submit"
              :disabled="submitting || !normalizedSlug"
            >
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {{ submitting ? "Opening…" : "Continue to Sign In" }}
              <i v-if="!submitting" class="ti ti-arrow-right ms-2"></i>
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              data-bs-dismiss="modal"
              :disabled="submitting"
            >
              Cancel
            </button>
          </div>

          <p class="text-center text-muted small mt-3 mb-0">
            Don’t have a workspace yet?
            <a :href="registerUrl" class="fw-semibold">Start a free trial</a>
          </p>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Modal } from "bootstrap";
import {
  slugifyWorkspaceName,
  tenantDomainLabel,
  centralMarketingUrl,
  isTenancyFrontendEnabled,
} from "@/utils/hostname";
import {
  lookupWorkspace,
  workspaceHostPreview,
  workspaceLoginUrl,
} from "@/utils/workspaceLookup";

const props = defineProps({
  modalId: { type: String, default: "workspace_sign_in_modal" },
});

const emit = defineEmits(["opened", "closed"]);

const slug = ref("");
const status = ref(""); // '', checking, found, missing, error
const error = ref("");
const submitting = ref(false);
const inputRef = ref(null);

let modalInstance = null;
let lookupTimer = null;
let lookupController = null;

const domainLabel = computed(() => tenantDomainLabel());
const normalizedSlug = computed(() => slugifyWorkspaceName(slug.value));
const previewHost = computed(() =>
  normalizedSlug.value ? workspaceHostPreview(normalizedSlug.value) : ""
);
const registerUrl = computed(() => centralMarketingUrl("/register"));

function onSlugInput() {
  error.value = "";
  status.value = "";
  scheduleLookup();
}

function scheduleLookup() {
  if (lookupTimer) clearTimeout(lookupTimer);
  if (lookupController) {
    lookupController.abort();
    lookupController = null;
  }

  const value = normalizedSlug.value;
  if (!value) {
    status.value = "";
    return;
  }

  lookupTimer = setTimeout(async () => {
    lookupController = new AbortController();
    status.value = "checking";
    error.value = "";

    try {
      const result = await lookupWorkspace(value, {
        signal: lookupController.signal,
      });

      if (slugifyWorkspaceName(slug.value) !== value) return;

      if (result.exists) {
        status.value = "found";
        error.value = "";
        return;
      }

      status.value = "missing";
      error.value = result.reserved
        ? "This workspace URL is reserved."
        : "Workspace not found. Check the spelling and try again.";
    } catch (e) {
      if (e?.code === "ERR_CANCELED") return;
      if (slugifyWorkspaceName(slug.value) !== value) return;
      status.value = "error";
      error.value = "Could not verify that workspace right now. Try again.";
    } finally {
      lookupController = null;
    }
  }, 350);
}

async function continueToWorkspace() {
  const value = normalizedSlug.value;
  if (!value || submitting.value) return;

  submitting.value = true;
  error.value = "";

  try {
    const result = await lookupWorkspace(value);
    if (!result.exists) {
      status.value = "missing";
      error.value = result.reserved
        ? "This workspace URL is reserved."
        : "Workspace not found. Check the spelling and try again.";
      return;
    }

    const target =
      result.redirectUrl || workspaceLoginUrl(value);
    if (!target) {
      error.value = "Could not build the login URL for that workspace.";
      return;
    }

    window.location.assign(target);
  } catch {
    error.value = "Could not open that workspace right now. Try again.";
  } finally {
    submitting.value = false;
  }
}

async function open(prefill = "") {
  if (!isTenancyFrontendEnabled()) {
    window.location.assign("/login");
    return;
  }

  slug.value = slugifyWorkspaceName(prefill);
  error.value = "";
  status.value = "";
  submitting.value = false;

  modalInstance?.show();
  emit("opened");

  await nextTick();
  inputRef.value?.focus();
  if (slug.value) scheduleLookup();
}

function close() {
  modalInstance?.hide();
}

onMounted(() => {
  const el = document.getElementById(props.modalId);
  if (!el) return;

  modalInstance = Modal.getOrCreateInstance(el, { backdrop: "static" });

  el.addEventListener("hidden.bs.modal", () => {
    emit("closed");
  });
  el.addEventListener("shown.bs.modal", () => {
    inputRef.value?.focus();
  });
});

onBeforeUnmount(() => {
  if (lookupTimer) clearTimeout(lookupTimer);
  if (lookupController) lookupController.abort();
  modalInstance?.hide();
  modalInstance = null;
});

watch(
  () => slug.value,
  (value) => {
    const cleaned = slugifyWorkspaceName(value);
    if (value && value !== cleaned && !value.endsWith("-")) {
      // keep typing fluid; only normalize on submit/lookup
    }
  }
);

defineExpose({ open, close });
</script>

<style scoped>
.dq-ws-card {
  border: 0;
  border-radius: 1.15rem;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
}

.dq-ws-header {
  padding: 1.35rem 1.35rem 0.5rem;
}

.dq-ws-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(254, 138, 34, 0.12);
  color: #fe8a22;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.dq-ws-subtitle {
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.45;
}

.dq-ws-body {
  padding: 0.75rem 1.35rem 1.5rem;
}

.dq-ws-input-wrap {
  display: flex;
  align-items: stretch;
  border: 1.5px solid #e2e8f0;
  border-radius: 0.85rem;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.dq-ws-input-wrap:focus-within {
  border-color: #fe8a22;
  box-shadow: 0 0 0 3px rgba(254, 138, 34, 0.15);
}

.dq-ws-input-wrap.is-error {
  border-color: #f87171;
}

.dq-ws-input-wrap.is-ok {
  border-color: #34d399;
}

.dq-ws-scheme,
.dq-ws-domain {
  display: inline-flex;
  align-items: center;
  padding: 0 0.75rem;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.88rem;
  white-space: nowrap;
}

.dq-ws-input {
  border: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  padding-left: 0.35rem;
  padding-right: 0.35rem;
  font-weight: 600;
  color: #0f172a;
}

.dq-ws-preview {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #64748b;
  font-size: 0.82rem;
}

.dq-ws-preview i {
  color: #94a3b8;
}

.dq-ws-status {
  font-size: 0.88rem;
  display: flex;
  align-items: center;
}

.dq-ws-hint {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  padding: 0.75rem 0.9rem;
  border-radius: 0.75rem;
  background: #fff7ed;
  color: #9a3412;
  font-size: 0.82rem;
  line-height: 1.4;
}

.dq-ws-hint i {
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.dq-ws-submit {
  font-weight: 600;
}
</style>
